import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { api } from '../lib/api';
import type { Poem } from './poemsSlice';

const customBaseQuery: BaseQueryFn<
  { endpoint: string; args?: any },
  unknown,
  unknown
> = async ({ endpoint, args }) => {
  try {
    const result = await (api as any)[endpoint](args);
    return { data: result };
  } catch (axiosError) {
    const err = axiosError as any;
    return {
      error: {
        status: err.status,
        data: err.message || err.data || 'Error',
      },
    };
  }
};

const normalizeTags = (input: unknown): string[] => {
  if (!input) return [];
  if (Array.isArray(input)) return input.map(String);
  if (typeof input === 'string') {
    const raw = input.trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch (_) {
      // Fall through to comma-separated parsing.
    }
    return raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeStatus = (value: any): Poem['status'] => {
  if (typeof value === 'string' && value.trim()) {
    const upper = value.toUpperCase();
    if (['DRAFT', 'REVIEW', 'PUBLISHED', 'REJECTED'].includes(upper)) {
      return upper as Poem['status'];
    }
  }
  return undefined;
};

const normalizePoem = (p: any): Poem => {
  const status = normalizeStatus(p.status) ??
    (p.published === true ? 'PUBLISHED' : p.published === false ? 'DRAFT' : undefined);
  return {
    id: Number(p.id),
    title: String(p.title ?? ''),
    language: String(p.language ?? 'en'),
    author: p.author ?? undefined,
    content: String(p.content ?? ''),
    tags: normalizeTags(p.tags),
    category: p.category ?? undefined,
    status,
    published: status ? status === 'PUBLISHED' : Boolean(p.published ?? true),
    createdAt: p.createdAt ?? undefined,
    updatedAt: p.updatedAt ?? undefined,
    description: p.description ?? undefined,
    mood: p.mood ?? undefined,
    theme: p.theme ?? undefined,
    country: p.country ?? undefined,
    createdBy: p.createdBy ?? undefined,
    createdByUsername: p.createdByUsername ?? undefined,
    likeCount: Number.isFinite(p.likeCount) ? Number(p.likeCount) : 0,
    bookmarkCount: Number.isFinite(p.bookmarkCount) ? Number(p.bookmarkCount) : 0,
    shareCount: Number.isFinite(p.shareCount) ? Number(p.shareCount) : 0,
    copyCount: Number.isFinite(p.copyCount) ? Number(p.copyCount) : 0,
    viewCount: Number.isFinite(p.viewCount) ? Number(p.viewCount) : 0,
    likedByCurrentUser: Boolean(p.likedByCurrentUser ?? false),
    bookmarkedByCurrentUser: Boolean(p.bookmarkedByCurrentUser ?? false),
  };
};

const unwrapPoemPayload = (
  payload: unknown,
  depth = 0
): { list: unknown[]; found: boolean } => {
  if (Array.isArray(payload)) {
    return { list: payload, found: true };
  }

  if (!payload || typeof payload !== 'object' || depth > 3) {
    return { list: [], found: false };
  }

  const container = payload as Record<string, unknown>;
  const preferredKeys = [
    'items',
    'data',
    'results',
    'poems',
    'content',
    'records',
    'rows',
  ];

  for (const key of preferredKeys) {
    if (key in container) {
      const nested = unwrapPoemPayload(container[key], depth + 1);
      if (nested.found) {
        return nested;
      }
    }
  }

  for (const value of Object.values(container)) {
    const nested = unwrapPoemPayload(value, depth + 1);
    if (nested.found) {
      return nested;
    }
  }

  return { list: [], found: false };
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: customBaseQuery,
  tagTypes: ['Poem', 'Favorite'],
  endpoints: builder => ({
    getPoems: builder.query<Poem[], { scope?: string; status?: string | string[] } | void>({
      query: (args) => ({ endpoint: 'listPoems', args }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Poem' as const, id })),
              { type: 'Poem', id: 'LIST' },
            ]
          : [{ type: 'Poem', id: 'LIST' }],
      transformResponse: (response: unknown) => {
        const { list, found } = unwrapPoemPayload(response);
        if (!found) return [];
        return (Array.isArray(list) ? list : []).map(normalizePoem);
      },
    }),
    getPoem: builder.query<Poem, string>({
      query: (id) => ({ endpoint: 'getPoem', args: id }),
      providesTags: (result, error, id) => [{ type: 'Poem', id: Number(id) }],
      transformResponse: (response: unknown) => normalizePoem(response),
    }),
    getFavorites: builder.query<Poem[], 'like' | 'bookmark' | 'all' | void>({
      query: (type) => ({ endpoint: 'listFavorites', args: type }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Favorite' as const, id })),
              { type: 'Favorite', id: 'LIST' },
            ]
          : [{ type: 'Favorite', id: 'LIST' }],
      transformResponse: (response: unknown) => {
        const { list, found } = unwrapPoemPayload(response);
        if (!found) return [];
        return (Array.isArray(list) ? list : []).map(normalizePoem);
      },
    }),
    toggleLike: builder.mutation<Poem, number>({
      query: (id) => ({ endpoint: 'togglePoemLike', args: id }),
      invalidatesTags: (result, error, id) => [
        { type: 'Poem', id },
        { type: 'Favorite', id: 'LIST' }
      ],
      transformResponse: (response: unknown) => normalizePoem(response),
    }),
    toggleBookmark: builder.mutation<Poem, number>({
      query: (id) => ({ endpoint: 'togglePoemBookmark', args: id }),
      invalidatesTags: (result, error, id) => [
        { type: 'Poem', id },
        { type: 'Favorite', id: 'LIST' }
      ],
      transformResponse: (response: unknown) => normalizePoem(response),
    }),
    deletePoem: builder.mutation<number, number>({
      query: (id) => ({ endpoint: 'deletePoem', args: id }),
      invalidatesTags: (result, error, id) => [{ type: 'Poem', id: 'LIST' }],
    }),
    createPoem: builder.mutation<Poem, Omit<Poem, 'id'>>({
      query: (poem) => ({ endpoint: 'createPoem', args: poem }),
      invalidatesTags: [{ type: 'Poem', id: 'LIST' }],
    }),
    updatePoem: builder.mutation<Poem, { id: number } & Omit<Poem, 'id'>>({
      query: ({ id, ...payload }) => ({ endpoint: 'updatePoem', args: { id, ...payload } }),
      invalidatesTags: (result, error, arg) => [{ type: 'Poem', id: arg.id }],
    }),
    recordShare: builder.mutation<Poem, number>({
      query: (id) => ({ endpoint: 'recordPoemShare', args: id }),
      invalidatesTags: (result, error, id) => [{ type: 'Poem', id }],
    }),
    recordCopy: builder.mutation<Poem, number>({
      query: (id) => ({ endpoint: 'recordPoemCopy', args: id }),
      invalidatesTags: (result, error, id) => [{ type: 'Poem', id }],
    }),
    recordView: builder.mutation<Poem, number>({
      query: (id) => ({ endpoint: 'recordPoemView', args: id }),
      invalidatesTags: (result, error, id) => [{ type: 'Poem', id }],
    }),
  }),
});

export const {
  useGetPoemsQuery,
  useGetPoemQuery,
  useGetFavoritesQuery,
  useToggleLikeMutation,
  useToggleBookmarkMutation,
  useDeletePoemMutation,
  useCreatePoemMutation,
  useUpdatePoemMutation,
  useRecordShareMutation,
  useRecordCopyMutation,
  useRecordViewMutation,
} = apiSlice;
